import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ref, get, set, serverTimestamp } from 'firebase/database';
import { db } from '../lib/firebase.js';
import { useAuth } from '../lib/useAuth.jsx';
import {
  GAMES,
  GAMES_LIST,
  DOMAINS,
  LEVELS,
  GOALS,
  domainForGoal,
  CATALOG_VERSION,
} from '../data/gamesCatalog.js';
import './GamesCatalog.css';

// Admin-visible inventory of every game in the Brain Health catalog.
// Useful for:
//   - Sanity-checking the data file renders correctly
//   - Spot-checking that every game's image URL resolves
//   - Reviewing the goal/level matrix at a glance
//   - Reseeding the Firebase mirror from this data file (super-admin)
//
// This page reads directly from the data file at build time for the
// rendered inventory — no Firebase, no fetch, no loading state. The
// Firebase mirror under /brain_health/games_catalog/ is what the
// resident dashboard reads at runtime for recommendations, and it's
// regenerated from this file via the "Reseed Catalog" button below.
//
// Schema note: games declare `goals: ['A','D', ...]` (goal keys, first
// is primary). Each goal maps to a cognitive domain via GOALS, which
// supplies the icon/color/label. Games do NOT carry domains directly.
//
// Reseed contract:
//   - Writes the ENTIRE catalog node in one atomic set() so renames
//     and deletions in the data file propagate (a merge would leave
//     stale games behind).
//   - Gated to the super-admin. The route is already wrapped in
//     RequireSuperAdmin, and the database rules independently gate
//     the write on the super-admin email — this button just surfaces
//     it. Non-super-admins never see the control.
//   - Idempotent: writing the same version twice is harmless.
export default function GamesCatalog() {
  const { isSuperAdmin } = useAuth();

  // Drift state: compares the version stored in Firebase against the
  // CATALOG_VERSION baked into this build.
  //   'loading'  — still reading the mirror
  //   'missing'  — node has never been seeded
  //   'insync'   — Firebase version matches this build
  //   'stale'    — Firebase version differs (older or newer)
  //   'error'    — the read failed
  const [driftState, setDriftState] = useState('loading');
  const [firebaseVersion, setFirebaseVersion] = useState(null);
  const [driftError, setDriftError] = useState(null);

  // Reseed action state.
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null); // { ok: bool, message }

  // Read the current mirror version on mount (super-admin only —
  // the node is world-readable, but there's no reason to fetch it
  // for community admins who can't act on it, and this page is
  // super-admin-gated anyway).
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;

    (async () => {
      try {
        const snap = await get(ref(db, 'brain_health/games_catalog/version'));
        if (cancelled) return;
        const val = snap.val();
        if (val == null) {
          setDriftState('missing');
          setFirebaseVersion(null);
        } else {
          setFirebaseVersion(val);
          setDriftState(val === CATALOG_VERSION ? 'insync' : 'stale');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to read catalog mirror version:', err);
        setDriftError(err.message);
        setDriftState('error');
      }
    })();

    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  async function handleReseed() {
    setSeeding(true);
    setSeedResult(null);
    try {
      // Build the full mirror payload. We mirror the data file's
      // canonical objects verbatim, plus version + seed metadata.
      const payload = {
        version: CATALOG_VERSION,
        seeded_at: serverTimestamp(),
        domains: DOMAINS,
        levels: LEVELS,
        goals: GOALS,
        games: GAMES,
      };

      // One atomic write of the entire node — replaces whatever was
      // there so renames/removals propagate cleanly.
      await set(ref(db, 'brain_health/games_catalog'), payload);

      setFirebaseVersion(CATALOG_VERSION);
      setDriftState('insync');
      setSeedResult({
        ok: true,
        message: `Catalog seeded — ${GAMES_LIST.length} games at v${CATALOG_VERSION}.`,
      });
    } catch (err) {
      console.error('Reseed failed:', err);
      setSeedResult({
        ok: false,
        message: `Reseed failed: ${err.message}`,
      });
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="container catalog">
      <span className="eyebrow fade-in-1">Administrator · Games Catalog</span>
      <h1 className="catalog-title fade-in-2">
        The <em>Brain Health</em> game library.
      </h1>

      <div className="catalog-meta fade-in-3">
        <span className="catalog-meta-item">
          <strong>{GAMES_LIST.length}</strong> games
        </span>
        <span className="catalog-meta-divider">·</span>
        <span className="catalog-meta-item">
          <strong>{Object.keys(DOMAINS).length}</strong> cognitive domains
        </span>
        <span className="catalog-meta-divider">·</span>
        <span className="catalog-meta-item">
          <strong>{Object.keys(LEVELS).length}</strong> assessment levels
        </span>
        <span className="catalog-meta-divider">·</span>
        <span className="catalog-meta-item catalog-version">
          v{CATALOG_VERSION}
        </span>
      </div>

      {/* -----------------------------------------------------------
          Reseed panel — super-admin only.
          Inline styles (not GamesCatalog.css) so this drops in
          without a CSS deploy and dodges the caching mismatch that
          bites the React app on style-only changes.
          ----------------------------------------------------------- */}
      {isSuperAdmin ? (
        <div
          className="fade-in-3"
          style={{
            marginTop: 'var(--space-5)',
            padding: 'var(--space-4)',
            border: '1px solid var(--paper-edge)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--warm-white)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--space-3)',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  fontWeight: 700,
                  color: 'var(--ink-faded)',
                }}
              >
                Firebase mirror
              </span>
              <DriftLine
                state={driftState}
                firebaseVersion={firebaseVersion}
                driftError={driftError}
              />
            </div>

            <button
              type="button"
              className="btn"
              onClick={handleReseed}
              disabled={seeding}
              style={{ flexShrink: 0 }}
            >
              {seeding ? 'Reseeding…' : 'Reseed Catalog'}
            </button>
          </div>

          {seedResult ? (
            <p
              role={seedResult.ok ? 'status' : 'alert'}
              style={{
                margin: 0,
                fontFamily: 'var(--font-serif)',
                fontSize: 'var(--text-sm)',
                color: seedResult.ok ? 'var(--ink-soft)' : '#a11',
              }}
            >
              {seedResult.message}
            </p>
          ) : (
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-serif)',
                fontSize: 'var(--text-sm)',
                color: 'var(--ink-faded)',
                lineHeight: 1.5,
              }}
            >
              Pushes this build's catalog to{' '}
              <code>/brain_health/games_catalog/</code>, which the resident
              dashboard reads for recommendations. Run this after editing the
              catalog data file (and bumping its version).
            </p>
          )}
        </div>
      ) : null}

      <hr className="editorial-rule" />

      {/* Section: domains */}
      <section className="catalog-section fade-in-4">
        <h2 className="catalog-section-title">Cognitive Domains</h2>
        <div className="catalog-domains">
          {Object.values(DOMAINS).map((domain) => (
            <div key={domain.key} className="catalog-domain-card">
              <div
                className="catalog-domain-mark"
                style={{ background: domain.color }}
                aria-hidden="true"
              >
                {domain.icon}
              </div>
              <div className="catalog-domain-body">
                <h3 className="catalog-domain-name">{domain.label}</h3>
                <span className="catalog-domain-clinical">
                  {domain.clinical}
                </span>
                <p className="catalog-domain-desc">{domain.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section: goals */}
      <section className="catalog-section">
        <h2 className="catalog-section-title">Resident Goals</h2>
        <p className="catalog-section-lede">
          During onboarding, residents pick two or three of these in their
          own words. Each goal maps to a cognitive domain.
        </p>
        <ul className="catalog-goals">
          {GOALS.map((goal) => {
            const domain = DOMAINS[goal.domain];
            return (
              <li key={goal.key} className="catalog-goal">
                <span className="catalog-goal-label">
                  <span
                    aria-hidden="true"
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 700,
                      color: 'var(--ink-faded)',
                      marginRight: '0.5rem',
                    }}
                  >
                    {goal.key}.
                  </span>
                  {goal.label}
                </span>
                <span className="catalog-goal-example">{goal.example}</span>
                <span className="catalog-goal-domain">
                  {domain ? domain.label : goal.domain}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Section: levels */}
      <section className="catalog-section">
        <h2 className="catalog-section-title">Assessment Levels</h2>
        <table className="catalog-levels-table">
          <thead>
            <tr>
              <th>Level</th>
              <th>Score range</th>
              <th>Pacing</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(LEVELS).map((level) => (
              <tr key={level.key}>
                <td>{level.label}</td>
                <td className="catalog-mono">{level.range}</td>
                <td>{level.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Section: games */}
      <section className="catalog-section">
        <h2 className="catalog-section-title">Games</h2>
        <div className="catalog-games">
          {GAMES_LIST.map((game) => {
            // Resolve the game's goal keys to domain objects for the
            // tags. The first goal is primary; the rest are secondary.
            const goalKeys = Array.isArray(game.goals) ? game.goals : [];
            const resolved = goalKeys.map((key) => ({
              key,
              domain: domainForGoal(key),
            }));
            const primary = resolved[0];

            // Defensive: a game with no goals, or whose primary goal
            // key isn't in GOALS, must not white-screen the whole
            // catalog. Surface it as a visible warning card so the bad
            // record is easy to find and fix, then move on.
            if (!primary || !primary.domain) {
              const problem = goalKeys.length === 0
                ? 'has no `goals` array'
                : `references unknown goal key "${goalKeys[0]}"`;
              return (
                <article
                  key={game.id || Math.random()}
                  className="catalog-game"
                  style={{ borderColor: '#a11' }}
                >
                  <div
                    className="catalog-game-header"
                    style={{ display: 'block' }}
                  >
                    <h3 className="catalog-game-name" style={{ color: '#a11' }}>
                      ⚠ {game.name || game.id || 'Unnamed game'}
                    </h3>
                    <p className="catalog-game-desc">
                      This catalog entry {problem}. Fix it in{' '}
                      <code>gamesCatalog.js</code> before reseeding.
                    </p>
                  </div>
                </article>
              );
            }

            const secondary = resolved.slice(1).filter((r) => r.domain);

            return (
              <article key={game.id} className="catalog-game">
                <div className="catalog-game-header">
                  <img
                    src={game.image}
                    alt=""
                    className="catalog-game-image"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="catalog-game-heading">
                    <h3 className="catalog-game-name">{game.name}</h3>
                    <p className="catalog-game-desc">
                      {game.short_description}
                    </p>
                    <div className="catalog-game-domains">
                      <span
                        className="catalog-tag catalog-tag-primary"
                        style={{
                          borderColor: primary.domain.color,
                          color: primary.domain.color,
                        }}
                      >
                        {primary.domain.label}
                      </span>
                      {secondary.map((r) => (
                        <span
                          key={r.key}
                          className="catalog-tag catalog-tag-secondary"
                          style={{ color: r.domain.color }}
                        >
                          + {r.domain.label}
                        </span>
                      ))}
                      <span className="catalog-tag-time">
                        ~{game.estimated_minutes} min
                      </span>
                    </div>
                  </div>
                </div>

                <div className="catalog-game-levels">
                  {Object.values(LEVELS).map((level) => {
                    const entry = game.levels[level.key];
                    // With the new schema, single-mode games use
                    // { mode: null } at every level (still "on"), while
                    // unavailable levels are absent/null. Show the mode
                    // when present, "•" for available-no-mode, "—" off.
                    const isOn = entry != null;
                    const modeLabel = isOn
                      ? (entry.mode != null ? entry.mode : '•')
                      : '—';
                    return (
                      <div
                        key={level.key}
                        className={
                          isOn
                            ? 'catalog-level-cell catalog-level-cell-on'
                            : 'catalog-level-cell catalog-level-cell-off'
                        }
                      >
                        <span className="catalog-level-cell-label">
                          {level.label}
                        </span>
                        <span className="catalog-level-cell-mode">
                          {modeLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="catalog-game-footer">
                  <a
                    href={game.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="catalog-game-link"
                  >
                    {game.url} ↗
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <hr className="editorial-rule" />

      <p className="catalog-back">
        <Link to="/admin" className="btn btn-secondary">
          ← Back to overview
        </Link>
      </p>
    </div>
  );
}

// =================================================================
// DriftLine — renders the sync status between this build's catalog
// version and the version currently in Firebase.
// =================================================================
function DriftLine({ state, firebaseVersion, driftError }) {
  const base = {
    fontFamily: 'var(--font-serif)',
    fontSize: 'var(--text-sm)',
    margin: 0,
  };

  if (state === 'loading') {
    return (
      <span style={{ ...base, fontStyle: 'italic', color: 'var(--ink-faded)' }}>
        Checking mirror…
      </span>
    );
  }

  if (state === 'error') {
    return (
      <span style={{ ...base, color: '#a11' }}>
        Couldn't read mirror{driftError ? `: ${driftError}` : ''}.
      </span>
    );
  }

  if (state === 'missing') {
    return (
      <span style={{ ...base, color: 'var(--ink-soft)' }}>
        <Dot color="#c47f00" /> Never seeded — press Reseed to publish.
      </span>
    );
  }

  if (state === 'insync') {
    return (
      <span style={{ ...base, color: 'var(--ink-soft)' }}>
        <Dot color="#1a7f37" /> In sync at v{firebaseVersion}.
      </span>
    );
  }

  // stale
  return (
    <span style={{ ...base, color: 'var(--ink-soft)' }}>
      <Dot color="#c47f00" /> Out of date — mirror is v{firebaseVersion}, this
      build is v{CATALOG_VERSION}.
    </span>
  );
}

function Dot({ color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: '0.6rem',
        height: '0.6rem',
        borderRadius: '50%',
        background: color,
        marginRight: '0.4rem',
        verticalAlign: 'baseline',
      }}
    />
  );
}
